import { useApplicationModals } from "app/common/hooks";
import { Badge } from "app/components/atoms/badge";
import {
  BadgeButton,
  ServerSidePaginationButtons,
} from "app/components/atoms/button";
import { FormHeader } from "app/components/atoms/layout";
import { SearchInput } from "app/components/atoms/input";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import api from "app/integration/api";
import { useConfirm } from "app/components/molecules/confirm";
import { usePaginatedCollector } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const breadcrumbItems = [
  { label: "Gudang", href: "#" },
  { label: "Transfer Gudang", href: "#" },
];

const statusOptions = [
  { value: null, label: "Semua Status" },
  { value: "draft", label: "Draft" },
  { value: "in_transit", label: "Dalam Perjalanan" },
  { value: "received", label: "Diterima" },
  { value: "canceled", label: "Dibatalkan" },
];

const statusBadgeColor = (status) => {
  switch (status) {
    case "draft":
      return "gray";
    case "in_transit":
      return "blue";
    case "received":
      return "green";
    case "canceled":
      return "red";
    default:
      return "gray";
  }
};

const statusLabel = (status) =>
  statusOptions.find((option) => option.value === status)?.label ?? status;

const warehouseLabel = (id, warehouses) =>
  id == null
    ? "Gudang Utama"
    : warehouses.find((w) => w.id === id)?.name ?? "Gudang #" + id;

function WarehouseTransferList() {
  const { confirm, ConfirmDialog } = useConfirm({ testId: "warehouse-transfer-confirm" });
  const navigate = useNavigate();
  const { openModal, setPayloads } = useApplicationModals();

  const [warehouses, setWarehouses] = useState([]);
  const [actionLoadingIds, setActionLoadingIds] = useState([]);

  const [params, setParams] = useState({
    search: "",
    status: null,
    source_warehouse_id: null,
    dest_warehouse_id: null,
    page: 1,
    limit: 25,
  });

  const [
    warehouseTransfers,
    setWarehouseTransfers,
    warehouseTransfersLoader,
    collectWarehouseTransfers,
    warehouseTransferPaginationProps,
  ] = usePaginatedCollector({ modelName: "warehouseTransfer", params });

  const showError = (title, err) => {
    setPayloads("errorMessage.view", {
      title,
      message: err?.response?.data?.message ?? "Terjadi kesalahan",
    });
    openModal("errorMessage.view");
  };

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
        showError("Transfer Gudang", err);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseFilterOptions = [
    { value: null, label: "Semua Gudang" },
    { value: "null", label: "Gudang Utama" },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
    })),
  ];

  const handleAction = async (action, id, code) => {
    const confirmMessage =
      action === "ship"
        ? "Apakah Anda yakin ingin mengirim transfer " + code + "?"
        : "Apakah Anda yakin ingin membatalkan transfer " + code + "?";
    if (!(await confirm(confirmMessage))) return;

    setActionLoadingIds((prev) => [...prev, id]);
    try {
      if (action === "ship") {
        await api.warehouseTransfer.ship(id);
      } else {
        await api.warehouseTransfer.cancel(id);
      }
      await collectWarehouseTransfers();
    } catch (err) {
      showError("Transfer Gudang", err);
    } finally {
      setActionLoadingIds((prev) => prev.filter((itemId) => itemId !== id));
    }
  };

  return (
    <MainLayout
      activeSidebarNavigation="warehouse"
      activeSidebarChild="warehouse-transfer"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Transfer Gudang"
      headingButtons={[
        {
          label: "+ Buat Transfer",
          type: "routerButton",
          to: "/warehouse-transfer/create",
        },
      ]}
    >
      <AppModals
        items={["errorMessage.view"]}
        onSuccess={{ "errorMessage.view": () => {} }}
      />
      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <FormHeader appearance="none">
              <SearchInput testId="warehouse-transfer-search-input"
                placeholder="Cari Kode Transfer"
                type="text"
                value={params.search}
                onChange={(e) =>
                  setParams({ ...params, search: e.target.value, page: 1 })
                }
              />
              <Select testId="warehouse-transfer-status-select"
                label="Status"
                placeholder="Filter Status"
                options={statusOptions}
                value={params.status}
                onChange={(e) =>
                  setParams({ ...params, status: e.target.value, page: 1 })
                }
              />
              <Select testId="warehouse-transfer-gudang-asal-select"
                label="Gudang Asal"
                placeholder="Filter Gudang Asal"
                options={warehouseFilterOptions}
                value={params.source_warehouse_id}
                onChange={(e) =>
                  setParams({
                    ...params,
                    source_warehouse_id: e.target.value,
                    page: 1,
                  })
                }
              />
              <Select testId="warehouse-transfer-gudang-tujuan-select"
                label="Gudang Tujuan"
                placeholder="Filter Gudang Tujuan"
                options={warehouseFilterOptions}
                value={params.dest_warehouse_id}
                onChange={(e) =>
                  setParams({
                    ...params,
                    dest_warehouse_id: e.target.value,
                    page: 1,
                  })
                }
              />
            </FormHeader>
          </Table.SearchBarWrapper>
        </Table.UtilitiesWrapper>
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Kode</Table.Heading>
              <Table.Heading>Asal</Table.Heading>
              <Table.Heading>Tujuan</Table.Heading>
              <Table.Heading>Status</Table.Heading>
              <Table.Heading>Aksi</Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {warehouseTransfers?.map((warehouseTransfer, key) => (
              <Table.BodyRow key={key}>
                <Table.Cell>{warehouseTransfer?.code}</Table.Cell>
                <Table.Cell>
                  {warehouseLabel(
                    warehouseTransfer?.source_warehouse_id,
                    warehouses
                  )}
                </Table.Cell>
                <Table.Cell>
                  {warehouseLabel(
                    warehouseTransfer?.dest_warehouse_id,
                    warehouses
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Badge color={statusBadgeColor(warehouseTransfer?.status)}>
                    {statusLabel(warehouseTransfer?.status)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex gap-1">
                    <BadgeButton testId="warehouse-transfer-detail-button"
                      onClick={() =>
                        navigate("/warehouse-transfer/" + warehouseTransfer?.id)
                      }
                    >
                      Detail
                    </BadgeButton>
                    {warehouseTransfer?.status === "draft" && (
                      <BadgeButton testId="warehouse-transfer-kirim-button"
                        color="green"
                        useLoadingState={true}
                        loading={actionLoadingIds.includes(
                          warehouseTransfer?.id
                        )}
                        onClick={() =>
                          handleAction(
                            "ship",
                            warehouseTransfer?.id,
                            warehouseTransfer?.code
                          )
                        }
                      >
                        Kirim
                      </BadgeButton>
                    )}
                    {(warehouseTransfer?.status === "draft" ||
                      warehouseTransfer?.status === "in_transit") && (
                      <BadgeButton testId="warehouse-transfer-batal-button"
                        color="red"
                        useLoadingState={true}
                        loading={actionLoadingIds.includes(
                          warehouseTransfer?.id
                        )}
                        onClick={() =>
                          handleAction(
                            "cancel",
                            warehouseTransfer?.id,
                            warehouseTransfer?.code
                          )
                        }
                      >
                        Batal
                      </BadgeButton>
                    )}
                  </div>
                </Table.Cell>
              </Table.BodyRow>
            ))}
          </Table.Body>
        </Table.Wrapper>
        <Table.PaginationWrapper>
          <ServerSidePaginationButtons
            paginationProps={warehouseTransferPaginationProps}
            params={params}
            setParams={setParams}
          />
        </Table.PaginationWrapper>
      </Table.Container>
      {ConfirmDialog}
    </MainLayout>
  );
}

export default WarehouseTransferList;
